import { useCallback, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Button, Checkbox, tokens, Text } from '@hotjar/hotsauce';
import { useStore } from '@hotjar/recordings/src/store/player/context';
import { useTrack } from '@hotjar/tracking';
import { event as HJEvent } from '@hotjar/tracking/src/tools/hotjar';

import { getRoute } from '../../../../../utils/routing';
import { PLAYBACKS } from '../../../../../constants/routes';
import { generateQueryFormatFilterForSimilarRecordings } from '../utils/generateQueryFormatFilterForSimilarRecordings';
import { getRecordingValueForOption } from '../utils/getRecordingValueForOption';
import type { Recording } from '../../../types';
import { SimilarRecordingsFilterLabel } from '../SimilarRecordingsFilterLabel/SimilarRecordingsFilterLabel';
import type { SimilarRecordingsFilterOption } from '../types';
import { SimilarRecordingsFilterOptions } from '../constants';

const StyledCheckboxContainer = styled(Checkbox)`
  display: flex;
  align-items: center;
  max-width: 100%;
  margin-top: ${tokens.spacer.x2};

  & > * {
    min-width: 0;
  }
`;

const StyledActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${tokens.spacer.x1};
  margin-top: ${tokens.spacer.x5};
`;

type SimilarRecordingsPopupProps = {
  onClose: () => void;
};

const UnMemoSimilarRecordingsPopup = ({ onClose }: SimilarRecordingsPopupProps): JSX.Element => {
  const { click } = useTrack();
  const { t } = useTranslation('common');

  const store = useStore();
  const state = store.getState();

  const { sortBy } = state;
  // Coercion is safe because the component won't render if recording is null
  const recording = state.recording as Recording;

  const [availableFilters] = useState(
    // Don't render option to select a filter if the recording is missing that value
    () =>
      Object.values(SimilarRecordingsFilterOptions).filter((filter) =>
        getRecordingValueForOption(filter, recording),
      ),
  );

  const [selectedFilters, setSelectedFilters] = useState(availableFilters);

  const toggleFilter = useCallback(
    (filterOption: SimilarRecordingsFilterOption) => {
      const updatedFilters = selectedFilters.includes(filterOption)
        ? selectedFilters.filter((filter) => filter !== filterOption)
        : [...selectedFilters, filterOption];

      setSelectedFilters(updatedFilters);
    },
    [selectedFilters],
  );

  const viewSimilarRecordingsList = useCallback(() => {
    // Tracking
    click('Similar recordings popup - view', 'session_info', {
      state_event_was_triggered_on: `Selected filters: ${selectedFilters}`,
    });
    HJEvent('View similar recordings clicked');

    // Creates filters based on selected options and recording's session info
    const filters = generateQueryFormatFilterForSimilarRecordings(recording, selectedFilters);

    // Open recordings list with created filters
    window.open(
      `${getRoute(PLAYBACKS.LIST.path, {
        siteId: recording.site_id,
      })}?sort_by=${sortBy}&filters=${encodeURI(JSON.stringify(filters))}&view=list`,
    );

    onClose();
  }, [click, sortBy, recording, selectedFilters, onClose]);

  return (
    <div data-testid="player-similar-recording-section-popup">
      <Text textType="body2">
        {t('player.session-info.similar-recordings-session.popup.description')}
      </Text>
      {availableFilters.map((filter) => (
        <StyledCheckboxContainer
          key={`similar-recording-filter-${filter}`}
          labelContent={
            <SimilarRecordingsFilterLabel
              data-testid={`similar-recording-filter-${filter}`}
              filter={filter}
              recording={recording}
            />
          }
          isChecked={selectedFilters.includes(filter)}
          onChange={() => toggleFilter(filter)}
        />
      ))}
      <StyledActions>
        <Button
          kind="ghost"
          size="s"
          onClick={onClose}
          data-testid="player-similar-recording-popup-button-close"
        >
          {t('player.session-info.similar-recordings-session.popup.button.cancel')}
        </Button>
        <Button
          size="s"
          onClick={viewSimilarRecordingsList}
          data-testid="player-similar-recording-popup-button-view"
          isDisabled={selectedFilters.length === 0}
        >
          {t('player.session-info.similar-recordings-session.popup.button.view')}
        </Button>
      </StyledActions>
    </div>
  );
};

export const SimilarRecordingsPopup = memo(UnMemoSimilarRecordingsPopup);
